import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import Webcam from 'react-webcam';
import { b64toBlob } from '@/lib/utils/b64toBlob';
import { withS3Bucket } from '@/js/shared/components/file-upload/services/s3bucket';

interface UploadedFile {
  generated: string;
  original: string;
  result?: number;
}

export interface IProps {
  // Injected by withS3Bucket
  upload?: (file: File, callback: (fileInfo: UploadedFile) => unknown) => void;
  onChange: (fileInfo: UploadedFile) => unknown;
  label?: string;
  mirrored?: boolean;
  maxWidth?: any;
}

export interface IStyleProps {
  maxWidth?: any;
}

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: 'user',
};

const useStyles = (theme: Theme, maxWidth: IStyleProps) => ({
  container: {
    display: 'inline-block !important',
    maxWidth: `${maxWidth}px !important`,
    textAlign: 'center !important',
  },
  webcam: {
    borderRadius: '5px !important',
    overflow: 'hidden !important',
    '& video': {
      display: 'block !important',
    },
    position: 'relative !important',
    backgroundColor: '#333 !important',
  },
  buttonHolder: {
    position: 'relative !important',
    display: 'inline-block !important',
    margin: '-6px auto 0 auto !important',
    backgroundColor: `${theme.palette.background.default} !important`,
    borderRadius: '5px !important',
    padding: '0 8px !important',
  },
  waiting: {
    position: 'absolute !important',
    inset: '0 !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
  },
  switchCam: {
    position: 'absolute !important',
    top: '5px !important',
    right: '5px !important',
    backgroundColor: '#000000AA !important',
    borderRadius: '20px !important',
    padding: '2px !important',
    '& span': {
      fontSize: '1.2rem !important',
      color: '#ffffff !important',
    },
    '&:hover': {
      backgroundColor: '#000000 !important',
    },
  },
});

export const WebcamUploader = withS3Bucket()(
  ({
    upload,
    onChange,
    label = 'Snap!',
    mirrored = false,
    maxWidth = 300,
  }: IProps) => {
    const theme = useTheme();
    const classes = useStyles(theme, maxWidth);
    const [ready, setReady] = useState(false);
    const [uploading, setUploading] = useState(false);
    const webcamRef = React.useRef(null);
    const [deviceId, setDeviceId] = useState('');
    const [devices, setDevices] = useState([]);
    const [error, setError] = useState<string | null>(null);
    const [mediaSupported, setMediaSupported] = useState<boolean | null>(null);

    const handleDevices = useCallback(
      (mediaDevices: any) =>
        setDevices(
          mediaDevices.filter(
            ({ kind }: { kind: string }) => kind === 'videoinput'
          )
        ),
      [setDevices]
    );

    const enumerateDevices = useCallback(async () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        console.warn('navigator.mediaDevices.enumerateDevices not available');
        return;
      }
      try {
        const mediaDevices = await navigator.mediaDevices.enumerateDevices();
        handleDevices(mediaDevices);
      } catch (error) {
        console.error('Error enumerating devices:', error);
      }
    }, [handleDevices]);

    const rotateCamera = () => {
      console.log('rotate', deviceId, devices);

      const currentCamIndex = devices.findIndex(
        (cam) => cam.deviceId === deviceId
      );
      if (devices.length > 0) {
        setDeviceId(devices[(currentCamIndex + 1) % devices.length].deviceId);
      }
    };

    useEffect(() => {
      if (devices.length > 1 && !deviceId) {
        setDeviceId(devices[0].deviceId);
      }
    }, [devices.length, deviceId]);

    const capture = React.useCallback(async () => {
      setUploading(true);
      // No forced dimensions: the canvas takes the camera's native size so
      // portrait / 4:3 streams are not stretched to 16:9
      const imageSrc = webcamRef.current.getScreenshot();
      upload(
        new File(
          [
            b64toBlob(
              imageSrc.replace(/^data:image\/(png|jpeg|jpg);base64,/, ''),
              'image/jpeg'
            ),
          ],
          `webcam.jpg`
        ),
        (fileInfo: UploadedFile) => {
          onChange(fileInfo);
          //   setTransfered((processed: any[]) => [...processed, process]);
          setUploading(false);
        }
      );
    }, [webcamRef, upload, onChange]);
    const mediaError = (error: string | DOMException) => {
      console.error('Camera error:', error);

      let errorMessage = 'Could not connect to camera';

      if (error instanceof DOMException) {
        switch (error.name) {
          case 'NotAllowedError':
            errorMessage =
              'Camera permission denied. Please allow camera access in your browser settings.';
            break;
          case 'NotFoundError':
            errorMessage =
              'No camera found. Please connect a camera and try again.';
            break;
          case 'NotReadableError':
            errorMessage = 'Camera is already in use by another application.';
            break;
          case 'OverconstrainedError':
            errorMessage = 'Camera does not support the requested settings.';
            break;
          case 'SecurityError':
            errorMessage =
              'Camera access blocked. Please use HTTPS or localhost.';
            break;
          case 'TypeError':
            errorMessage = 'Camera access not supported in this browser.';
            break;
          default:
            errorMessage = `Camera error: ${error.message || error.name}`;
        }
      } else if (typeof error === 'string') {
        // Handle string errors from react-webcam
        if (
          error.includes('getUserMedia is not implemented') ||
          error.includes('getUserMedia is not a function')
        ) {
          errorMessage =
            'Camera access is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.';
        } else {
          errorMessage = error;
        }
      }

      setError(errorMessage);
      setReady(false);
    };
    const cameraReady = () => {
      setReady(true);
      setError(null);
      // Enumerate devices after camera permissions are granted
      // This ensures device labels are available
      enumerateDevices();
    };

    // Check if mediaDevices is available on mount
    useEffect(() => {
      const checkMediaSupport = () => {
        // Check for modern MediaDevices API
        if (!navigator.mediaDevices) {
          setError(
            'Camera access is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.'
          );
          setMediaSupported(false);
          return false;
        }

        // Check for getUserMedia method
        if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
          setError(
            'Camera access is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.'
          );
          setMediaSupported(false);
          return false;
        }

        // Check if we're on a secure context (HTTPS or localhost)
        if (!window.isSecureContext) {
          setError(
            'Camera access requires a secure connection. Please use HTTPS or localhost to access the camera.'
          );
          setMediaSupported(false);
          return false;
        }

        setMediaSupported(true);
        return true;
      };

      checkMediaSupport();
    }, []);

    // Listen for device changes
    useEffect(() => {
      if (!navigator.mediaDevices?.addEventListener) {
        return;
      }
      navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
      return () => {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          enumerateDevices
        );
      };
    }, [enumerateDevices]);
    console.log('mediaSupported', mediaSupported);
    console.log('error', error);
    // /visitor/conferences/:id/signmediaupload
    return (
      <Box sx={classes.container}>
        <Box sx={classes.webcam}>
          {mediaSupported !== false && (
            <Webcam
              audio={false}
              height={'auto'}
              screenshotFormat="image/jpeg"
              screenshotQuality={0.7}
              forceScreenshotSourceSize
              width={'100%'}
              ref={webcamRef}
              mirrored={mirrored}
              videoConstraints={
                deviceId
                  ? {
                      width: videoConstraints.width,
                      height: videoConstraints.height,
                      deviceId: { exact: deviceId },
                    }
                  : videoConstraints
              }
              onUserMedia={cameraReady}
              onUserMediaError={mediaError}
            />
          )}
          {(!ready || uploading) && !error && (
            <Box sx={classes.waiting}>
              <CircularProgress size={20} />
            </Box>
          )}

          {error && (
            <Box
              sx={{
                ...classes.waiting,
                position: 'relative',
                flexDirection: 'column',
                gap: 1,
                padding: 2,
                textAlign: 'center',
              }}
              id="error-box"
            >
              <Icon sx={{ color: 'error.main', fontSize: '2rem' }}>
                videocam_off
              </Icon>

              <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                {error}
              </Box>
            </Box>
          )}
          {devices.length > 1 && (
            <Box sx={classes.switchCam}>
              <Tooltip title="Switch camera" aria-label="Switch camera">
                <IconButton size="small" onClick={rotateCamera}>
                  <Icon>flip_camera_ios</Icon>
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box sx={classes.buttonHolder}>
          <Button
            disabled={uploading}
            startIcon={<Icon>camera_alt</Icon>}
            onClick={capture}
            color="primary"
            size="small"
          >
            {label}
          </Button>
        </Box>
      </Box>
    );
  }
);
